import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, IconButton, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AlbumItem } from '../../types/board.types';
import './AlbumChoice.scss';

interface AlbumChoiceProps {
    open: boolean;
    onClose: () => void;
    albums: AlbumItem[];
    onMove: (albumId: string | null) => void;
    currentAlbumId?: string | null;
}

export default function AlbumChoice({ open, onClose, albums, onMove, currentAlbumId }: AlbumChoiceProps) {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        if (open && currentAlbumId) {
            setSelectedId(currentAlbumId);
        } else if (open && !currentAlbumId) {
            setSelectedId(null);
        }
    }, [open, currentAlbumId]);

    const handleMove = () => {
        if (selectedId === null) {
            // 移动到上一级（移出画册）
            onMove(null);
        } else if (selectedId) {
            onMove(selectedId);
        } else {
            return;
        }
        setSelectedId(null);
        onClose();
    };

    const handleClose = () => {
        setSelectedId(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            fullScreen={isMobile}
            sx={{
                '& .MuiDialog-paper': {
                    width: isMobile ? '100%' : 520,
                    height: isMobile ? '100%' : 560,
                    maxWidth: 'none',
                    borderRadius: isMobile ? 0 : '16px',
                    boxShadow: '0 6px 20px var(--c-shadow-light)',
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}
        >
            {/* 弹窗头部 */}
            <div className="album-choice-header">
                <h2 className="album-choice-title">移动绘板</h2>
                <IconButton onClick={handleClose} className="album-choice-close" size="small">
                    <CloseIcon sx={{ fontSize: 24, color: 'var(--c-text-primary)' }} />
                </IconButton>
            </div>

            {/* 分割线 */}
            <div className="album-choice-divider" />

            {/* 画册列表 */}
            <div className="album-choice-list">
                {/* 当在画册内时，显示“绘板空间（移动至上一级）” */}
                {currentAlbumId && (
                    <div
                        className={`album-choice-item ${selectedId === null ? 'selected' : ''}`}
                        onClick={() => setSelectedId(null)}
                    >
                        <div className={`album-choice-radio ${selectedId === null ? 'checked' : ''}`}>
                            {selectedId === null && <span className="album-choice-radio-check">✓</span>}
                        </div>
                        <span className="album-choice-item-name">绘板空间<span style={{color: 'var(--c-text-secondary)'}}>（移动至上一级）</span></span>
                    </div>
                )}
                {albums.length === 0 ? (
                    !currentAlbumId && <div className="album-choice-empty">暂无画册</div>
                ) : (
                    albums.map((album) => {
                        const isCurrent = album.albumId === currentAlbumId;
                        const coverSlots = album.covers.slice(0, 4);

                        return (
                            <div
                                key={album.albumId}
                                className={`album-choice-item ${selectedId === album.albumId ? 'selected' : ''}`}
                                onClick={() => setSelectedId(album.albumId)}
                            >
                                <div className={`album-choice-radio ${selectedId === album.albumId ? 'checked' : ''}`}>
                                    {selectedId === album.albumId && <span className="album-choice-radio-check">✓</span>}
                                </div>
                                <span className="album-choice-item-name">{album.albumName}</span>
                                <div className="album-choice-thumbnails">
                                    {Array.from({ length: 4 }).map((_, i) => {
                                        const cover = coverSlots[i];
                                        return (
                                            <div
                                                key={i}
                                                className="album-choice-thumb"
                                                style={cover ? { backgroundImage: `url(${cover})` } : {}}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* 底部按钮栏 */}
            <div className="album-choice-footer">
                <button className="album-choice-btn album-choice-btn-cancel" onClick={handleClose}>取消</button>
                <button
                    className="album-choice-btn album-choice-btn-move"
                    disabled={selectedId === null && !currentAlbumId}
                    onClick={handleMove}
                >
                    移动
                </button>
            </div>
        </Dialog>
    );
}
